/*
 * Bara Bros — Desktop header search dropdown + image-search modal.
 * Reuses the theme's existing trapFocus/removeTrapFocus globals
 * (assets/global.js) and the body/html scroll-lock convention already
 * used by assets/cart-drawer.js, instead of reimplementing them.
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 300;
  var HISTORY_KEY = 'bb_search_history';

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(null, args);
      }, wait);
    };
  }

  function escapeHtml(value) {
    var map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return map[c];
    });
  }

  function getHistory() {
    try {
      var raw = localStorage.getItem(HISTORY_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      return [];
    }
  }

  function saveHistory(arr) {
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(arr));
    } catch (e) {
      /* localStorage unavailable (private mode, quota) — history just won't persist */
    }
  }

  function addHistoryTerm(term, max) {
    term = (term || '').trim();
    if (!term) return;
    var arr = getHistory().filter(function (t) {
      return t.toLowerCase() !== term.toLowerCase();
    });
    arr.unshift(term);
    saveHistory(arr.slice(0, max));
  }

  function removeHistoryTerm(term, max) {
    var arr = getHistory().filter(function (t) {
      return t !== term;
    });
    saveHistory(arr.slice(0, max));
  }

  /* ---------------- Search dropdown panel ---------------- */

  function initSearchPanel(root) {
    if (root.dataset.bbInitialized) return;
    root.dataset.bbInitialized = 'true';

    var configEl = root.querySelector('script[data-bb-header-search-config]');
    var config = {};
    try {
      config = configEl ? JSON.parse(configEl.textContent) : {};
    } catch (e) {
      config = {};
    }

    var input = root.querySelector('[data-bb-search-input]');
    var dropdown = root.querySelector('[data-bb-dropdown]');
    if (!input || !dropdown) return;

    var historyChips = root.querySelector('[data-bb-history-chips]');
    var clearBtn = root.querySelector('[data-bb-clear-history]');
    var staticGroups = root.querySelector('[data-bb-static-groups]');
    var liveResults = root.querySelector('[data-bb-live-results]');
    var emptyState = root.querySelector('[data-bb-empty-state]');
    var maxHistory = config.maxHistory || 8;
    var maxProducts = config.maxProducts || 5;
    var abortController = null;

    function runSearch(term) {
      addHistoryTerm(term, maxHistory);
      input.value = term;
      if (input.form) input.form.submit();
    }

    function renderHistory() {
      if (!historyChips) return;
      var items = getHistory().slice(0, maxHistory);
      historyChips.innerHTML = '';

      items.forEach(function (term) {
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'bb-header-search__history-chip';

        var label = document.createElement('span');
        label.textContent = term;
        chip.appendChild(label);

        var remove = document.createElement('span');
        remove.className = 'bb-header-search__history-chip-remove';
        remove.setAttribute('aria-hidden', 'true');
        remove.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>';
        remove.addEventListener('click', function (evt) {
          evt.stopPropagation();
          removeHistoryTerm(term, maxHistory);
          renderHistory();
        });
        chip.appendChild(remove);

        chip.addEventListener('click', function () {
          runSearch(term);
        });

        historyChips.appendChild(chip);
      });

      var historyCol = historyChips.closest('.bb-header-search__col');
      var historyHasItems = config.showHistory !== false && items.length > 0;
      if (historyCol) historyCol.hidden = !historyHasItems;

      if (emptyState) {
        emptyState.hidden = historyHasItems || staticGroupsHaveContent();
      }
    }

    function staticGroupsHaveContent() {
      if (!staticGroups) return false;
      var trendingChips = staticGroups.querySelector('.bb-header-search__col--trending .bb-header-search__chips');
      var collectionsList = staticGroups.querySelector('[data-bb-collections-list]');
      var productsGrid = staticGroups.querySelector('[data-bb-products-grid]');
      return (
        (!!trendingChips && trendingChips.children.length > 0) ||
        (!!collectionsList && collectionsList.children.length > 0) ||
        (!!productsGrid && productsGrid.children.length > 0)
      );
    }

    function openDropdown() {
      dropdown.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }

    function closeDropdown() {
      dropdown.hidden = true;
      input.setAttribute('aria-expanded', 'false');
    }

    function showStatic() {
      if (staticGroups) staticGroups.hidden = false;
      if (liveResults) {
        liveResults.hidden = true;
        liveResults.innerHTML = '';
      }
      renderHistory();
    }

    function showLive() {
      if (staticGroups) staticGroups.hidden = true;
      if (liveResults) liveResults.hidden = false;
    }

    function renderLiveResults(data) {
      if (!liveResults) return;
      var results = (data && data.resources && data.resources.results) || {};
      var products = results.products || [];
      var collections = results.collections || [];
      var queries = results.queries || [];

      if (!products.length && !collections.length && !queries.length) {
        liveResults.innerHTML = '';
        if (emptyState) emptyState.hidden = false;
        return;
      }
      if (emptyState) emptyState.hidden = true;

      var html = '';

      if (queries.length) {
        html += '<div class="bb-header-search__col bb-header-search__col--trending">' +
          '<div class="bb-header-search__col-head"><span>' + escapeHtml(config.trendingHeading || 'Suggestions') + '</span></div>' +
          '<div class="bb-header-search__chips">';
        queries.forEach(function (q) {
          html += '<button type="button" class="bb-header-search__trend-chip" data-bb-live-term="' + escapeHtml(q.text) + '"><span>' + escapeHtml(q.text) + '</span></button>';
        });
        html += '</div></div>';
      }

      if (collections.length) {
        html += '<div class="bb-header-search__col bb-header-search__col--collections">' +
          '<div class="bb-header-search__col-head"><span>' + escapeHtml(config.collectionsHeading || 'Collections') + '</span></div>' +
          '<ul class="bb-header-search__list">';
        collections.forEach(function (c) {
          html += '<li class="bb-header-search__suggested-collection"><a class="bb-header-search__suggested-collection-link" href="' + c.url + '" tabindex="-1">' + escapeHtml(c.title) + '</a></li>';
        });
        html += '</ul></div>';
      }

      if (products.length) {
        html += '<div class="bb-header-search__col bb-header-search__col--products">' +
          '<div class="bb-header-search__col-head"><span>' + escapeHtml(config.productsHeading || 'Products') + '</span></div>' +
          '<ul class="bb-header-search__product-grid">';
        products.slice(0, maxProducts).forEach(function (p) {
          var img = p.image || (p.featured_image && p.featured_image.url) || '';
          var price = p.price != null ? p.price : '';
          html += '<li class="bb-header-search__suggested-product"><a class="bb-header-search__suggested-product-link" href="' + p.url + '" tabindex="-1">' +
            '<span class="bb-header-search__suggested-product-image">' + (img ? '<img src="' + img + '" alt="" loading="lazy">' : '') + '</span>' +
            '<span class="bb-header-search__suggested-product-title">' + escapeHtml(p.title) + '</span>' +
            (price !== '' ? '<span class="bb-header-search__suggested-product-price">' + escapeHtml(price) + '</span>' : '') +
            '</a></li>';
        });
        html += '</ul></div>';
      }

      liveResults.innerHTML = html;

      liveResults.querySelectorAll('[data-bb-live-term]').forEach(function (chip) {
        chip.addEventListener('click', function () {
          runSearch(chip.getAttribute('data-bb-live-term'));
        });
      });
    }

    var fetchSuggestions = debounce(function (term) {
      if (!config.predictiveSearchUrl) return;
      if (abortController) abortController.abort();
      abortController = new AbortController();

      var params = new URLSearchParams();
      params.set('q', term);
      params.set('resources[type]', 'product,collection,query');
      params.set('resources[limit]', '10');

      fetch(config.predictiveSearchUrl + '?' + params.toString(), { signal: abortController.signal })
        .then(function (res) {
          return res.ok ? res.json() : null;
        })
        .then(function (data) {
          if (data) renderLiveResults(data);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          /* network/parse failure: leave current panel state as-is */
        });
    }, DEBOUNCE_MS);

    function handleQueryChange() {
      var term = input.value.trim();
      if (term.length >= 2) {
        showLive();
        fetchSuggestions(term);
      } else {
        showStatic();
      }
    }

    input.addEventListener('focus', function () {
      renderHistory();
      openDropdown();
      handleQueryChange();
    });

    input.addEventListener('input', handleQueryChange);

    root.addEventListener('keydown', function (evt) {
      if (evt.key === 'Escape') {
        closeDropdown();
        input.blur();
      }
    });

    document.addEventListener('click', function (evt) {
      if (!root.contains(evt.target)) closeDropdown();
    });

    if (input.form) {
      input.form.addEventListener('submit', function (evt) {
        var term = input.value.trim();
        if (!term) {
          evt.preventDefault();
          return;
        }
        addHistoryTerm(term, maxHistory);
      });
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        saveHistory([]);
        renderHistory();
      });
    }

    root.querySelectorAll('[data-bb-search-term]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        var url = chip.getAttribute('data-bb-search-url');
        if (url) {
          window.location.href = url;
          return;
        }
        runSearch(chip.getAttribute('data-bb-search-term'));
      });
    });

    renderHistory();
  }

  if (!window.customElements.get('bb-header-search-panel')) {
    window.customElements.define(
      'bb-header-search-panel',
      class extends HTMLElement {
        connectedCallback() {
          initSearchPanel(this);
        }
      }
    );
  }

  /* ---------------- Image search modal ---------------- */

  var lastModalTrigger = null;

  function openImageSearchModal(modal, trigger) {
    modal.hidden = false;
    lastModalTrigger = trigger || null;
    document.body.classList.add('overflow-hidden');
    document.documentElement.setAttribute('scroll-lock', '');

    var content = modal.querySelector('.bb-header-search__image-modal-content');
    var closeBtn = modal.querySelector('[data-bb-image-search-close]');
    if (typeof window.trapFocus === 'function' && content) {
      window.trapFocus(content, closeBtn || content);
    }
  }

  function closeImageSearchModal(modal) {
    modal.hidden = true;
    document.body.classList.remove('overflow-hidden');
    document.documentElement.removeAttribute('scroll-lock');
    if (typeof window.removeTrapFocus === 'function') {
      window.removeTrapFocus(lastModalTrigger);
    }
  }

  function initImageSearchModals() {
    document.querySelectorAll('[data-bb-image-search-trigger]').forEach(function (trigger) {
      var modalId = trigger.getAttribute('data-bb-image-search-target');
      var modal = modalId ? document.getElementById(modalId) : null;
      if (!modal) return;
      trigger.addEventListener('click', function () {
        openImageSearchModal(modal, trigger);
      });
    });

    document.querySelectorAll('[data-bb-image-search-modal]').forEach(function (modal) {
      if (modal.dataset.bbInitialized) return;
      modal.dataset.bbInitialized = 'true';

      modal.querySelectorAll('[data-bb-image-search-close]').forEach(function (closeEl) {
        closeEl.addEventListener('click', function () {
          closeImageSearchModal(modal);
        });
      });

      modal.addEventListener('keydown', function (evt) {
        if (evt.key === 'Escape') closeImageSearchModal(modal);
      });

      var uploadZone = modal.querySelector('[data-bb-upload-zone]');
      var uploadInput = modal.querySelector('[data-bb-upload-input]');
      var uploadTrigger = modal.querySelector('[data-bb-upload-trigger]');
      var preview = modal.querySelector('[data-bb-upload-preview]');
      var previewImg = modal.querySelector('[data-bb-upload-preview-img]');
      var pasteInput = modal.querySelector('[data-bb-paste-link-input]');
      var message = modal.querySelector('[data-bb-image-search-message]');

      function showPreview(src) {
        if (!preview || !previewImg) return;
        var safeSrc = String(src).replace(/["\\]/g, '\\$&');
        previewImg.style.backgroundImage = 'url("' + safeSrc + '")';
        preview.hidden = false;
        if (message) message.hidden = false;
      }

      if (uploadTrigger && uploadInput) {
        uploadTrigger.addEventListener('click', function () {
          uploadInput.click();
        });
      }

      if (uploadInput) {
        uploadInput.addEventListener('change', function () {
          var file = uploadInput.files && uploadInput.files[0];
          if (!file) return;
          var reader = new FileReader();
          reader.onload = function (evt) {
            showPreview(evt.target.result);
          };
          reader.readAsDataURL(file);
        });
      }

      if (uploadZone) {
        ['dragenter', 'dragover'].forEach(function (evtName) {
          uploadZone.addEventListener(evtName, function (evt) {
            evt.preventDefault();
            uploadZone.classList.add('is-dragover');
          });
        });
        ['dragleave', 'drop'].forEach(function (evtName) {
          uploadZone.addEventListener(evtName, function (evt) {
            evt.preventDefault();
            uploadZone.classList.remove('is-dragover');
          });
        });
        uploadZone.addEventListener('drop', function (evt) {
          var file = evt.dataTransfer && evt.dataTransfer.files && evt.dataTransfer.files[0];
          if (!file || !file.type || file.type.indexOf('image/') !== 0) return;
          var reader = new FileReader();
          reader.onload = function (e) {
            showPreview(e.target.result);
          };
          reader.readAsDataURL(file);
        });
      }

      if (pasteInput) {
        pasteInput.addEventListener('input', debounce(function () {
          var url = pasteInput.value.trim();
          if (!url) return;
          showPreview(url);
        }, 400));
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initImageSearchModals);
  } else {
    initImageSearchModals();
  }
})();
